import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Key, Search, FileText, User, Calendar, ExternalLink, Share2 } from 'lucide-react';
import { collectionService } from '../../services/collectionService';
import { secretKeyService } from '../../services/secretKeyService';
import { CollectionItem } from '../../types';
import { useToast } from '../../context/ToastContext';

export const CollectionList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [collections, setCollections] = useState<CollectionItem[]>([]);
  const [ownedMap, setOwnedMap] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const limit = 20;

  useEffect(() => {
    loadCollections();
  }, [page]);

  useEffect(() => {
    const checkOwnerships = async () => {
      const map: Record<string, boolean> = {};
      for (const item of collections) {
        map[item.id] = await secretKeyService.isCollectionOwner(item);
      }
      setOwnedMap(map);
    };
    if (collections.length > 0) {
      checkOwnerships();
    }
  }, [collections]);

  const loadCollections = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await collectionService.fetchCollections(page, limit);
      if (res.data.length === 0 && page > 1) {
        setPage((prev) => prev - 1);
        return;
      }
      setCollections(res.data);
      setTotalItems(res.total);
    } catch (err: any) {
      setError(err.message || 'Không thể tải danh sách bộ sưu tập.');
    } finally {
      setLoading(false);
    }
  };

  const filteredCollections = collections.filter((item) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      item.name.toLowerCase().includes(q) ||
      (item.author && item.author.toLowerCase().includes(q))
    );
  });

  const totalPages = Math.ceil(totalItems / limit) || 1;

  return (
    <div style={{ maxWidth: '960px', margin: '0 auto', paddingBottom: '2rem' }}>
      {/* Top Header & Actions */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.875rem',
          marginBottom: '1.25rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-main)', margin: 0 }}>
              Bộ Sưu Tập Được Chia Sẻ
            </h2>
            <p style={{ fontSize: '0.8125rem', color: '#666', marginTop: '0.125rem' }}>
              Lưu trữ & chia sẻ từ điển Quick Translate
            </p>
          </div>
        </div>

        {/* Action Toolbar on 1 Row */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => navigate('/collections/create')}
            style={{
              flex: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem',
              backgroundColor: 'var(--primary-action)',
              border: 'none',
              color: '#0f172a',
              fontWeight: 700,
              fontSize: '0.875rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <Plus size={16} />
            <span>Tạo Bộ Sưu Tập</span>
          </button>

          <button
            onClick={() => navigate('/collections/keys')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem',
              backgroundColor: '#ffffff',
              border: '1px solid var(--border-light)',
              color: 'var(--text-on-light)',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <Key size={16} />
            <span>Mã khôi phục</span>
          </button>
        </div>
      </div>

      {/* Search Input */}
      <div
        style={{
          position: 'relative',
          marginBottom: '1.25rem',
        }}
      >
        <Search
          size={18}
          style={{
            position: 'absolute',
            left: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#94a3b8',
          }}
        />
        <input
          type="text"
          placeholder="Tìm theo tên hoặc tác giả..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '0.625rem 0.75rem 0.625rem 2.25rem',
            borderRadius: '0.5rem',
            border: '1px solid var(--border-light)',
            fontSize: '0.875rem',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Error Banner */}
      {error && (
        <div
          style={{
            backgroundColor: '#fee2e2',
            color: 'var(--accent-danger)',
            padding: '0.75rem 1rem',
            borderRadius: '0.5rem',
            marginBottom: '1.25rem',
            fontSize: '0.875rem',
          }}
        >
          {error}
        </div>
      )}

      {/* Skeleton Loading State */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                backgroundColor: 'var(--bg-card)',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-light)',
                padding: '1.25rem',
                height: '160px',
                animation: 'pulse 1.5s infinite ease-in-out',
              }}
            />
          ))}
        </div>
      ) : filteredCollections.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '3rem 1rem',
            backgroundColor: '#ffffff',
            borderRadius: '0.5rem',
            border: '1px solid var(--border-light)',
          }}
        >
          <FileText size={40} style={{ color: '#ccc', marginBottom: '0.5rem' }} />
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            {searchQuery ? 'Không tìm thấy bộ sưu tập nào phù hợp.' : 'Chưa có bộ sưu tập nào.'}
          </p>
        </div>
      ) : (
        /* Collection Grid Cards */
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
            gap: '1rem',
          }}
        >
          {filteredCollections.map((item) => (
            <div
              key={item.id}
              onClick={() => navigate(`/collections/${item.id}`)}
              style={{
                backgroundColor: 'var(--bg-card)',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-light)',
                padding: '1.25rem',
                cursor: 'pointer',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <h3
                    style={{
                      fontSize: '1.0625rem',
                      fontWeight: 700,
                      color: 'var(--text-on-light)',
                      margin: 0,
                      lineHeight: 1.3,
                    }}
                  >
                    {item.name}
                  </h3>
                  {ownedMap[item.id] && (
                    <span
                      style={{
                        padding: '0.125rem 0.375rem',
                        borderRadius: '9999px',
                        backgroundColor: '#dcfce7',
                        color: '#15803d',
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      Chủ sở hữu
                    </span>
                  )}
                </div>

                {/* Author Badge */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    fontSize: '0.8125rem',
                    color: '#555',
                    marginBottom: '0.625rem',
                  }}
                >
                  <User size={14} style={{ color: 'var(--text-on-light)' }} />
                  <span>Tác giả: <strong>{item.author || 'User-0000'}</strong></span>
                </div>

                {/* File Count & Types */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.875rem' }}>
                  {item.files && item.files.length > 0 ? (
                    item.files.slice(0, 4).map((f, idx) => (
                      <span
                        key={idx}
                        style={{
                          fontSize: '0.75rem',
                          padding: '0.125rem 0.375rem',
                          borderRadius: '0.25rem',
                          backgroundColor: 'var(--accent-highlight)',
                          color: 'var(--text-on-light)',
                          fontWeight: 500,
                        }}
                      >
                        {f.type}
                      </span>
                    ))
                  ) : (
                    <span style={{ fontSize: '0.75rem', color: '#888' }}>Chưa có file</span>
                  )}
                  {item.files && item.files.length > 4 && (
                    <span style={{ fontSize: '0.75rem', color: '#666', alignSelf: 'center' }}>
                      +{item.files.length - 4} file khác
                    </span>
                  )}
                </div>
              </div>

              {/* Bottom Meta */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '0.75rem',
                  borderTop: '1px solid rgba(0,0,0,0.06)',
                  fontSize: '0.75rem',
                  color: '#777',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Calendar size={12} />
                  <span>{new Date(item.createdAt).toLocaleDateString('vi-VN')}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const code = item.shortCode || item.id.slice(0, 8);
                      const shareUrl = `${window.location.origin}/c/${code}`;
                      navigator.clipboard.writeText(shareUrl);
                      toast.success('Đã sao chép link chia sẻ!');
                    }}
                    title="Sao chép link chia sẻ"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0.25rem',
                      borderRadius: '0.25rem',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#0284c7',
                      cursor: 'pointer',
                    }}
                  >
                    <Share2 size={13} />
                  </button>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      color: 'var(--primary-action)',
                      fontWeight: 600,
                    }}
                  >
                    <span>Chi tiết</span>
                    <ExternalLink size={12} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '2rem',
          }}
        >
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            style={{
              padding: '0.375rem 0.75rem',
              borderRadius: '0.375rem',
              border: '1px solid var(--border-light)',
              backgroundColor: '#ffffff',
              fontSize: '0.875rem',
              cursor: page <= 1 ? 'not-allowed' : 'pointer',
              opacity: page <= 1 ? 0.5 : 1,
            }}
          >
            Trang trước
          </button>
          <span style={{ fontSize: '0.875rem', color: '#555' }}>
            Trang {page} / {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            style={{
              padding: '0.375rem 0.75rem',
              borderRadius: '0.375rem',
              border: '1px solid var(--border-light)',
              backgroundColor: '#ffffff',
              fontSize: '0.875rem',
              cursor: page >= totalPages ? 'not-allowed' : 'pointer',
              opacity: page >= totalPages ? 0.5 : 1,
            }}
          >
            Trang sau
          </button>
        </div>
      )}
    </div>
  );
};
